import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { getAgentForRoles, DEFAULT_AGENT, isValidAgent } from '../roles/rolesConfig';

const AgentContext = createContext();

/**
 * AgentProvider - Manages current agent selection based on user roles
 */
export function AgentProvider({ children }) {
  const [currentAgent, setCurrentAgent] = useState(() => {
    // Try to load from sessionStorage
    const saved = sessionStorage.getItem('current-agent');
    return saved && isValidAgent(saved) ? saved : DEFAULT_AGENT;
  });

  const [userRoles, setUserRoles] = useState([]);
  const [userType, setUserType] = useState('individual'); // 'individual' | 'organization'
  const [organizationId, setOrganizationId] = useState(null);

  // Update agent when roles change
  useEffect(() => {
    if (userRoles.length > 0) {
      const agent = getAgentForRoles(userRoles);
      setCurrentAgent(agent);
      sessionStorage.setItem('current-agent', agent);
    }
  }, [userRoles]);

  // Save to sessionStorage when agent changes
  useEffect(() => {
    sessionStorage.setItem('current-agent', currentAgent);
  }, [currentAgent]);

  /**
   * Initialize agent based on user's Cognito roles
   * @param {Array<string>} roles - User's Cognito group names
   * @param {string} userType - 'individual' | 'organization'
   * @param {string} orgId - Organization ID (for org users)
   */
  const initializeAgent = useCallback((roles = [], userType = 'individual', orgId = null) => {
    setUserRoles(roles);
    setUserType(userType);
    setOrganizationId(orgId);
    const agent = getAgentForRoles(roles);
    setCurrentAgent(agent);
  }, []);

  /**
   * Manually switch agent (if user has access to multiple)
   * @param {string} agentId - Agent identifier
   */
  const switchAgent = useCallback((agentId) => {
    if (isValidAgent(agentId)) {
      setCurrentAgent(agentId);
      sessionStorage.setItem('current-agent', agentId);
    }
  }, []);

  /**
   * Clear agent on logout
   */
  const clearAgent = useCallback(() => {
    setCurrentAgent(DEFAULT_AGENT);
    setUserRoles([]);
    setUserType('individual');
    setOrganizationId(null);
    sessionStorage.removeItem('current-agent');
  }, []);

  const value = {
    currentAgent,
    userRoles,
    userType,
    organizationId,
    initializeAgent,
    switchAgent,
    clearAgent,
  };

  return <AgentContext.Provider value={value}>{children}</AgentContext.Provider>;
}

/**
 * Hook to use agent context
 */
export function useAgent() {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgent must be used within AgentProvider');
  }
  return context;
}
